import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import { ClaimRoleTenant, CreateMutation, IUseQuery, MutationResult, QueryHookParams, Role } from "../models";
import { useAuth } from "../contexts";

export interface IUseRoleQuery extends Pick<IUseQuery<Role>, "listQuery" | "detailQuery"> {
    addClaimMutation: CreateMutation<ClaimRoleTenant>;
    removeClaimMutation: MutationResult<boolean, unknown, ClaimRoleTenant, boolean>;
}

export const useRoleQuery = (params?: QueryHookParams): IUseRoleQuery => {
    const roleId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { get, post, del } = useAxios();
    const { isAuthorized } = useAuth();

    const listQuery = useGenericQuery({
        queryFn: (config) => get<Role[]>({ url: Endpoints.Roles, config }),
        queryKey: queryKeys.roles.list(),
        placeholderData: [],
        enabled: isAuthorized,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Role>({ url: `${Endpoints.Roles}/${roleId}`, config }),
        queryKey: queryKeys.roles.detail(roleId),
        enabled: isAuthorized && !!roleId,
    });

    const addClaimMutation = useGenericMutation({
        mutationFn: (data: Partial<ClaimRoleTenant>) =>
            post<Partial<ClaimRoleTenant>, ClaimRoleTenant>({ url: Endpoints.RoleClaims, data }),
        queryKey: queryKeys.roles.lists(),
        keysToInvalidate: [queryKeys.roles.lists(), queryKeys.roles.details()],
        successToast: "Permission added to role",
    });

    const removeClaimMutation = useGenericMutation({
        mutationFn: (data: ClaimRoleTenant) =>
            del({ url: `${Endpoints.RoleClaims}?claimId=${data.claimId}&roleId=${data.roleId}` }),
        queryKey: queryKeys.roles.lists(),
        keysToInvalidate: [queryKeys.roles.lists(), queryKeys.roles.details()],
        successToast: "Permission removed from role",
    });

    return useMemo<IUseRoleQuery>(
        () => ({
            listQuery,
            detailQuery,
            addClaimMutation,
            removeClaimMutation,
        }),
        [addClaimMutation, detailQuery, listQuery, removeClaimMutation],
    );
};
